/**
 * @file plan.js
 * NUEVA ARQUITECTURA DUAL - Sistema de planes Editor-N8N
 *
 * PRINCIPIOS:
 * - Dos campos: plan_data_pure (N8N) y plan_data_visual (Editor)
 * - Separación clara: datos de negocio vs visualización
 * - N8N consume solo plan_data_pure, Editor consume plan_data_visual
 * - Auto-layout para planes de N8N
 */

class PlanModel {
  /**
   * @param {Object} data - plan data
   * @param {Map<string, {color: string, name: string}>|null} rolesIndex
   *   Live role lookup keyed by rolId. When provided, the live role color
   *   overrides the snapshot saved in step.rolColor at read time.
   *   See Plan #70 (unified-role-color-system).
   */
  constructor(data = {}, rolesIndex = null) {
    // Campos base
    this.id = data.id || null;
    this.name = data.name || 'Plan sin título';
    this.objective = data.objective || 'Plan creado desde el editor';

    // ✅ CAMELCASE PURO: Sin fallbacks a snake_case
    this.inputData = data.inputData || '';
    this.planDataPure = data.planDataPure || null;
    this.planDataVisual = data.planDataVisual || null;

    // 🎨 Live color lookup — null when called from server (no React context).
    this.rolesIndex = rolesIndex;

    // Inicializar estructura de workflow
    this.initializeDataStructure(data);
    
    // Campos de relaciones (camelCase puro)
    this.initiatorRolId = data.initiatorRolId || null;
    this.userId = data.userId || null;
    this.createdBy = data.createdBy || null;
    
    // Campos de metadatos (camelCase puro)
    this.createdAt = data.createdAt || new Date().toISOString();
    this.updatedAt = data.updatedAt || new Date().toISOString();
    
    // Compatibilidad frontend
    this.status = data.status || 'activo';
    this.rolesCount = data.rolesCount || 0;

    // Agentes borrados que los pasos siguen referenciando (#458). Lo calcula el
    // backend (`planService.annotateMissingRoles`) leyendo el JSON, que es lo que
    // de verdad se ejecuta — `rolesCount` sale de la tabla puente y por eso el
    // plan parece más sano cuanto más roto está. Marca a nivel de plan: nunca
    // entra en planDataPure, que se reenvía tal cual al guardar.
    this.missingRoles = data.missingRoles || [];
    this.hasMissingRoles = data.hasMissingRoles || false;

    // Agentes archivados: la fila sigue en `rol`, y las queries de ejecución no
    // filtran archivados, así que el plan se guarda y se ejecuta igual. Se avisa
    // pero no se bloquea — se arregla desarchivando el agente. Estado distinto al
    // borrado, cubo distinto.
    this.archivedRoles = data.archivedRoles || [];
    this.hasArchivedRoles = data.hasArchivedRoles || false;
  }

  /**
   * ✅ NUEVA ARQUITECTURA: Inicializar estructura de datos
   */
  initializeDataStructure(data) {
    // 🐛 DEBUG: Log de datos recibidos
    console.log('[PlanModel] initializeDataStructure - datos recibidos:', {
      hasPlanDataVisual: !!this.planDataVisual,
      hasPlanDataPure: !!this.planDataPure,
      planDataVisual: this.planDataVisual,
      planDataPure: this.planDataPure,
      dataKeys: Object.keys(data)
    });
    
    if (this.planDataVisual) {
      // Plan completo del Editor
      console.log('[PlanModel] Usando planDataVisual');
      this.workflowData = this.parseVisualData(this.planDataVisual);
    } else if (this.planDataPure) {
      // Plan de N8N: generar visual automáticamente
      console.log('[PlanModel] Usando planDataPure - generando visual');
      this.workflowData = this.generateVisualFromPure(this.planDataPure);
    } else {
      // Plan nuevo vacío
      console.log('[PlanModel] Sin datos - creando workflow vacío');
      this.workflowData = this.createEmptyWorkflow();
    }
    
    // 🐛 DEBUG: Log del resultado
    console.log('[PlanModel] workflowData generado:', {
      nodeCount: this.workflowData.nodes?.length || 0,
      edgeCount: this.workflowData.edges?.length || 0,
      source: this.workflowData.metadata?.source
    });
  }

  /**
   * ✅ NUEVA FUNCIÓN: Parsear datos visuales
   */
  parseVisualData(planDataVisual) {
    const visual = planDataVisual.visual || {};
    const core = planDataVisual.core || {};
    
    return {
      nodes: this.convertStepsToNodes(core.steps || [], visual),
      edges: visual.edges || [],
      metadata: planDataVisual.metadata || {}
    };
  }

  /**
   * ✅ FUNCIÓN ACTUALIZADA: Generar visual desde pure
   * Estructura estándar OBLIGATORIA: { steps: [...], metadata: {...} }
   * Tanto N8N como Editor deben usar esta estructura
   */
  generateVisualFromPure(planDataPure) {
    // ✅ ESTRUCTURA ESTÁNDAR: N8N y Editor envían { steps: [...], metadata: {...} }
    let steps;
    if (planDataPure && planDataPure.steps) {
      // Estructura estándar con steps (tanto Editor como N8N)
      console.log('[PlanModel] generateVisualFromPure - Estructura estándar con steps');
      steps = planDataPure.steps;
    } else {
      // Error: estructura inválida
      console.error('[PlanModel] generateVisualFromPure - ERROR: Estructura inválida. Se requiere { steps: [...] }');
      throw new Error('plan_data_pure debe tener estructura con steps: { steps: [...], metadata: {...} }');
    }
    
    const nodes = steps.map((step, index) => ({
      id: `node_${step.id}`, // ✅ Formato consistente node_1, node_2, etc.
      type: 'agent',
      position: { x: 100 + (index * 250), y: 100 },
      data: {
        rolId: step.rolId,
        rolName: step.rolName,
        objectives: step.rolObjectives, // ✅ Objectives del rol (usado en fallback visual)
        label: step.label || step.rolObjectives, // ✅ Fallback: label personalizado → objectives del rol
        task: step.task,
        dataIn: Array.isArray(step.dataIn) ? step.dataIn.join(', ') : '',
        dataOut: Array.isArray(step.dataOut) ? step.dataOut.join(', ') : '',
        selectedTools: step.tools || [],
        // 🎨 Live color from rolesIndex; snapshot is anti-flash fallback only (Plan #70)
        color: this.rolesIndex?.get(step.rolId)?.color || step.rolColor,
        fullRolData: {
          id: step.rolId,
          name: step.rolName,
          color: step.rolColor,
          objectives: step.rolObjectives,
          prompt: step.rolePrompt
        }
      }
    }));

    // ✅ LÓGICA INTELIGENTE: Manejar execution_flow según el tipo de datos
    let executionFlow = [];
    if (Array.isArray(planDataPure)) {
      // N8N: no tiene execution_flow (se auto-genera)
      executionFlow = [];
    } else if (planDataPure && planDataPure.executionFlow) {
      // Editor: tiene execution_flow explícito (camelCase)
      executionFlow = planDataPure.executionFlow;
    }
    
    const edges = this.generateEdgesFromExecutionFlow(executionFlow, steps);
    
    return {
      nodes,
      edges,
      metadata: {
        source: 'n8n',
        auto_generated: true,
        generated_at: new Date().toISOString()
      }
    };
  }

  /**
   * ✅ NUEVA FUNCIÓN: Convertir steps a nodes
   */
  convertStepsToNodes(steps, visual) {
    return steps.map((step, index) => {
      // ✅ Usar directamente el nodeId guardado (estable)
      const nodeId = step.nodeId;
      
      console.log('[PlanModel] convertStepsToNodes - buscando step:', {
        stepId: step.id,
        nodeId: nodeId,
        visualNodesCount: visual.nodes?.length
      });
      
      // Buscar el nodo visual por su ID guardado
      const visualNode = visual.nodes?.find(n => n.id === nodeId) || {};
      
      if (!visualNode.id) {
        console.warn(`[PlanModel] ⚠️ No se encontró nodo visual para nodeId: ${nodeId}, usando posición por defecto`);
      }
    
        return {
        id: nodeId, // ✅ Usar nodeId estable guardado
        type: 'agent',
        position: visualNode.position || { x: 100 + (index * 250), y: 100 },
        data: {
          rolId: step.rolId,
          rolName: step.rolName,
          objectives: step.rolObjectives, // ✅ Objectives del rol (usado en fallback visual)
          label: step.label || step.rolObjectives, // ✅ Fallback: label personalizado → objectives del rol
          task: step.task,
          dataIn: Array.isArray(step.dataIn) ? step.dataIn.join(', ') : '',
          dataOut: Array.isArray(step.dataOut) ? step.dataOut.join(', ') : '',
          selectedTools: step.tools || [],
          // 🎨 Live color first, snapshot, visual style as last resort (Plan #70)
          color: this.rolesIndex?.get(step.rolId)?.color
              || step.rolColor
              || visualNode.style?.backgroundColor,
          fullRolData: {
            id: step.rolId,
            name: step.rolName,
            color: step.rolColor,
            objectives: step.rolObjectives,
            prompt: step.rolePrompt
          }
        }
      };
    });
  }

  createEmptyWorkflow() {
    return {
      nodes: [],
      edges: [],
      metadata: {
        created_at: new Date().toISOString(),
        source: 'empty'
      }
    };
  }


  /**
   * ✅ NUEVA FUNCIÓN: Convertir a formato Editor
   */
  toEditorFormat() {
    // 🐛 DEBUG: Log del workflow data
    console.log('[PlanModel] toEditorFormat - workflowData:', {
      hasWorkflowData: !!this.workflowData,
      nodeCount: this.workflowData?.nodes?.length || 0,
      edgeCount: this.workflowData?.edges?.length || 0,
      workflowData: this.workflowData
    });
    
    const result = {
      id: this.id,
      name: this.name,
      objective: this.objective,
      nodes: this.workflowData.nodes || [],
      edges: this.workflowData.edges || [],
      metadata: this.workflowData.metadata || {}
    };
    
    console.log('[PlanModel] toEditorFormat - resultado:', {
      nodeCount: result.nodes.length,
      edgeCount: result.edges.length
    });
    
    return result;
  }

  /**
   * ✅ NUEVA FUNCIÓN: Convertir a formato Backend
   * Envía en camelCase (el backend lo acepta)
   */
  toBackendFormat() {
    const editorData = this.toEditorFormat();

    return {
      id: this.id,
      name: this.name,
      objective: this.objective,
      inputData: this.inputData,
      planDataPure: this.generatePureData(editorData),
      planDataVisual: this.generateVisualData(editorData),
      initiatorRolId: this.initiatorRolId,
      userId: this.userId,
      createdBy: this.createdBy
    };
  }

  /**
   * ✅ NUEVA FUNCIÓN: Convertir a formato N8N
   * Retorna solo planDataPure (sin datos visuales)
   * Usado por N8N para ejecutar workflows
   */
  toN8NFormat() {
    // Si ya existe planDataPure, usarlo directamente
    if (this.planDataPure) {
      return this.planDataPure;
    }

    // Si no existe, generarlo desde editorData
    const editorData = this.toEditorFormat();
    return this.generatePureData(editorData);
  }

  /**
   * ✅ NUEVA FUNCIÓN: Generar datos puros para N8N
   */
  generatePureData(editorData) {
    // 🐛 DEBUG: Log de datos del editor
    console.log('[PlanModel] generatePureData - editorData recibido:', {
      nodeCount: editorData.nodes?.length || 0,
      edgeCount: editorData.edges?.length || 0,
      nodes: editorData.nodes,
      hasNodes: !!editorData.nodes
    });
    
    // ✅ LÓGICA INTELIGENTE: Solo reordenar si viene del Editor
    // N8N ya viene ordenado, Editor necesita reordenamiento dinámico
    let processedNodes = editorData.nodes || [];
    
    // Detectar si necesita reordenamiento (viene del Editor)
    const needsReordering = this.detectIfNeedsReordering(editorData);
    
    if (needsReordering) {
      console.log('[PlanModel] generatePureData - Reordenando nodos (origen: Editor)');
      processedNodes = this.orderNodesByExecutionFlow(editorData.nodes || [], editorData.edges || []);
    } else {
      console.log('[PlanModel] generatePureData - Manteniendo orden (origen: N8N o ya ordenado)');
    }
    
    const steps = processedNodes.map((node, index) => ({
      id: index + 1, // ✅ Refleja orden correcto según origen
      nodeId: node.id, // ✅ CRÍTICO: Guardar ID original del nodo para mapeo estable
      rolId: node.data.rolId,
      rolName: node.data.rolName,
      rolColor: node.data.color,
      rolObjectives: node.data.fullRolData?.objectives,
      rolePrompt: node.data.fullRolData?.prompt,
      label: node.data.label || 'Sin tarea', // ✅ Guardar label personalizado
      task: node.data.task,
      dataIn: node.data.dataIn ? node.data.dataIn.split(',').map(s => s.trim()) : [],
      dataOut: node.data.dataOut ? node.data.dataOut.split(',').map(s => s.trim()) : [],
      tools: node.data.selectedTools || []
    }));
    
    console.log('[PlanModel] generatePureData - steps generados:', steps.length, 'steps');
    
    return {
      version: "1.0",
      objective: this.objective,
      userId: this.userId, // ✅ Consistente con camelCase
      planSource: "editor",
      steps: steps,
      executionFlow: this.generateExecutionFlow(editorData.edges),
      metadata: {
        createdBy: "editor",
        llmOptimized: false,
        executionReady: true,
        dataSource: "editor",
        rolesResolved: true,
        toolsResolved: true,
        generatedAt: new Date().toISOString()
      }
    };
  }

  /**
   * ✅ NUEVA FUNCIÓN: Generar datos visuales completos
   */
  generateVisualData(editorData) {
    return {
      version: "1.0",
      core: {
        steps: this.generatePureData(editorData).steps
      },
      visual: {
        nodes: editorData.nodes.map(node => ({
          id: node.id, // ✅ ID simple del nodo
          position: node.position,
          style: {
            backgroundColor: node.data.color,
            borderColor: this.darkenColor(node.data.color)
          }
        })),
        edges: editorData.edges,
        layout: {
          type: "custom",
          auto_layout: false,
          created_in: "editor"
        }
      },
      metadata: {
        editor_version: "1.0.0",
        last_visual_update: new Date().toISOString(),
        canvas_settings: {}
      }
    };
  }

  // ✅ FUNCIONES HELPER
  
  /**
   * ✅ FUNCIÓN SIMPLIFICADA: Detectar si los nodos necesitan reordenamiento
   * N8N ya viene ordenado, Editor necesita reordenamiento dinámico
   */
  detectIfNeedsReordering(editorData) {
    // Criterio 1: Si es origen N8N (metadata), NO reordenar
    if (this.planDataPure?.metadata?.createdBy === 'n8n' || 
        this.planDataPure?.metadata?.planSource === 'n8n') {
      console.log('[PlanModel] detectIfNeedsReordering - Origen N8N detectado, NO reordenar');
      return false;
    }
    
    // Criterio 2: Si viene del Editor, SÍ reordenar
    if (editorData.workflowData || (editorData.nodes && editorData.edges)) {
      console.log('[PlanModel] detectIfNeedsReordering - Datos del Editor detectados, SÍ reordenar');
      return true;
    }
    
    // Por defecto: SÍ reordenar (Editor)
    console.log('[PlanModel] detectIfNeedsReordering - Por defecto: SÍ reordenar (Editor)');
    return true;
  }
  
  /**
   * ✅ NUEVA FUNCIÓN: Ordenar nodos según flujo de ejecución
   * Resuelve el problema de step.id incorrectos cuando se insertan nodos
   */
  orderNodesByExecutionFlow(nodes, edges) {
    if (!nodes || nodes.length === 0) return [];
    if (!edges || edges.length === 0) return nodes; // Sin conexiones, mantener orden original
    
    // 1. Crear mapa de nodos para acceso rápido
    const nodeMap = new Map();
    nodes.forEach(node => nodeMap.set(node.id, node));
    
    // 2. Construir grafo de dependencias (quién viene después de quién)
    const graph = new Map();
    const inDegree = new Map();
    
    // Inicializar todos los nodos
    nodes.forEach(node => {
      graph.set(node.id, []);
      inDegree.set(node.id, 0);
    });
    
    // Construir grafo desde edges
    edges.forEach(edge => {
      if (nodeMap.has(edge.source) && nodeMap.has(edge.target)) {
        graph.get(edge.source).push(edge.target);
        inDegree.set(edge.target, inDegree.get(edge.target) + 1);
      }
    });
    
    // 3. Ordenamiento topológico (algoritmo de Kahn)
    const queue = [];
    const result = [];
    
    // Encontrar nodos sin dependencias (grado de entrada = 0)
    inDegree.forEach((degree, nodeId) => {
      if (degree === 0) {
        queue.push(nodeId);
      }
    });
    
    // Procesar nodos en orden topológico
    while (queue.length > 0) {
      const currentNodeId = queue.shift();
      const currentNode = nodeMap.get(currentNodeId);
      
      if (currentNode) {
        result.push(currentNode);
        
        // Reducir grado de entrada de nodos dependientes
        const neighbors = graph.get(currentNodeId) || [];
        neighbors.forEach(neighborId => {
          inDegree.set(neighborId, inDegree.get(neighborId) - 1);
          if (inDegree.get(neighborId) === 0) {
            queue.push(neighborId);
          }
        });
      }
    }
    
    // 4. Manejar nodos desconectados (agregar al final)
    const processedIds = new Set(result.map(node => node.id));
    const unprocessedNodes = nodes.filter(node => !processedIds.has(node.id));
    
    console.log('[PlanModel] orderNodesByExecutionFlow:', {
      originalOrder: nodes.map(n => n.id),
      executionOrder: result.map(n => n.id),
      unprocessedNodes: unprocessedNodes.map(n => n.id)
    });
    
    return [...result, ...unprocessedNodes];
  }

  generateExecutionFlow(edges) {
    // ✅ FUNCIONA PARA AMBOS ESCENARIOS:
    // - N8N: Ya viene ordenado, solo mapea las conexiones
    // - Editor: Usa el orden recalculado por orderNodesByExecutionFlow
    // Ahora con IDs simples: node_1 → step 1, node_2 → step 2, etc.
    return edges.map(edge => {
      const sourceStep = this.getStepIdFromNodeId(edge.source);
      const targetStep = this.getStepIdFromNodeId(edge.target);
      return {
        from: sourceStep,
        to: targetStep,
        condition: null
      };
    });
  }

  /**
   * ✅ NUEVA FUNCIÓN MEJORADA: Auto-generar edges desde execution_flow o secuencialmente
   * - Si hay execution_flow explícito: usarlo
   * - Si NO hay execution_flow: generar conexiones secuenciales basadas en IDs de steps
   */
  generateEdgesFromExecutionFlow(executionFlow, steps) {
    console.log('[PlanModel] generateEdgesFromExecutionFlow - input:', { executionFlow, stepsCount: steps?.length });
    
    // Si hay execution_flow explícito, usarlo
    if (executionFlow && Array.isArray(executionFlow) && executionFlow.length > 0) {
      console.log('[PlanModel] Usando execution_flow explícito');
      const edges = executionFlow.map((flow, index) => ({
        id: `flow-edge-${flow.from}-${flow.to}`,
        source: `node_${flow.from}`,
        target: `node_${flow.to}`,
        type: 'custom',
        data: {}
      }));
      
      console.log('[PlanModel] Generated edges from execution_flow:', edges);
      return edges;
    }
    
    // ✅ NUEVA LÓGICA: Auto-generar secuencial desde IDs de steps
    if (steps && steps.length > 1) {
      console.log('[PlanModel] Auto-generando edges secuenciales desde step IDs');
      
      // Ordenar steps por ID para asegurar secuencia correcta
      const sortedSteps = [...steps].sort((a, b) => a.id - b.id);
      
      const autoEdges = [];
      for (let i = 0; i < sortedSteps.length - 1; i++) {
        const currentStep = sortedSteps[i];
        const nextStep = sortedSteps[i + 1];
        
        autoEdges.push({
          id: `auto-edge-${currentStep.id}-${nextStep.id}`,
          source: `node_${currentStep.id}`,
          target: `node_${nextStep.id}`,
          type: 'custom',
          data: { auto_generated: true }
        });
      }
      
      console.log('[PlanModel] Auto-generados:', autoEdges.length, 'edges secuenciales');
      return autoEdges;
    }
    
    console.log('[PlanModel] Sin execution_flow ni steps suficientes - returning empty');
    return [];
  }

  darkenColor(color) {
    if (!color || !color.startsWith('#')) return color;
    const hex = color.replace('#', '');
    const r = Math.max(0, parseInt(hex.substr(0, 2), 16) - 30);
    const g = Math.max(0, parseInt(hex.substr(2, 2), 16) - 30);
    const b = Math.max(0, parseInt(hex.substr(4, 2), 16) - 30);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  getStepIdFromNode(node) {
    return parseInt(node.id.replace(/\D/g, '')) || 1;
  }

  getStepIdFromNodeId(nodeId) {
    return parseInt(nodeId.replace(/\D/g, '')) || 1;
  }

  // ✅ MÉTODOS DE UTILIDAD
  toFrontendFormat() {
    return {
      id: this.id,
      name: this.name,
      description: this.objective,
      inputData: this.inputData,
      workflowData: this.toEditorFormat(),
      initiatorRolId: this.initiatorRolId,
      userId: this.userId,
      createdBy: this.createdBy,
      status: this.status,
      rolesCount: this.rolesCount,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      // #458 — la marca de agentes borrados y el JSON crudo que necesita la
      // cirugía de `planStepRemoval`. El JSON va aparte de `workflowData` a
      // propósito: guardar desde el canvas regenera los pasos desde los nodos y
      // renumera ids, mientras que quitar el paso roto debe dejar los demás
      // intactos byte a byte.
      missingRoles: this.missingRoles,
      hasMissingRoles: this.hasMissingRoles,
      archivedRoles: this.archivedRoles,
      hasArchivedRoles: this.hasArchivedRoles,
      planDataPure: this.planDataPure,
      planDataVisual: this.planDataVisual
    };
  }

  hasVisualData() {
    return !!this.planDataVisual;
  }

  isFromN8N() {
    return this.planDataPure && !this.planDataVisual;
  }

  // ✅ MÉTODOS ESTÁTICOS
  static fromEditor(editorData, rolesIndex = null) {
    // 🐛 DEBUG: Log de datos recibidos desde editor
    console.log('[PlanModel] fromEditor - editorData recibido:', {
      hasNodes: !!editorData.nodes,
      nodeCount: editorData.nodes?.length || 0,
      hasEdges: !!editorData.edges,
      edgeCount: editorData.edges?.length || 0,
      hasWorkflowData: !!editorData.workflowData,
      editorDataKeys: Object.keys(editorData)
    });

    // Crear una instancia básica con los campos base (camelCase puro)
    const planModel = new PlanModel({
      id: editorData.id,
      name: editorData.name,
      objective: editorData.objective || editorData.description,
      inputData: editorData.inputData,
      userId: editorData.userId,
      createdBy: editorData.createdBy || 'editor',
      initiatorRolId: editorData.initiatorRolId
    }, rolesIndex);

    // ✅ NUEVA LÓGICA: Asignar workflowData directamente desde los datos del editor
    planModel.workflowData = {
      nodes: editorData.nodes || [],
      edges: editorData.edges || [],
      metadata: {
        source: 'editor',
        created_at: new Date().toISOString()
      }
    };

    console.log('[PlanModel] fromEditor - workflowData asignado:', {
      nodeCount: planModel.workflowData.nodes.length,
      edgeCount: planModel.workflowData.edges.length
    });

    return planModel;
  }

  static fromBackend(backendData, rolesIndex = null) {
    return new PlanModel(backendData, rolesIndex);
  }

  static fromN8N(n8nData, rolesIndex = null) {
    return new PlanModel({
      planDataPure: n8nData,
      createdBy: 'n8n',
      source: 'n8n'
    }, rolesIndex);
  }

  static createNew(userId, name = 'Plan sin título', objective = '', rolesIndex = null) {
    return new PlanModel({
      name,
      objective,
      userId: userId,
      createdBy: 'editor'
    }, rolesIndex);
  }
}

export { PlanModel }; 
